import { isNativePlatform, getPlatform } from '../utils/platform';
import { encodedUsername } from '../utils/authentication';
import {
  toConversationDetail,
  toJobPostingSummary,
  toMyApplications,
  toInterviews,
  toJobSearchJobDetail,
} from '../routes';
import axios from 'axios';
import { App } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Badge } from '@capawesome/capacitor-badge';

const PUSH_TOKEN_KEY = 'dentalpost_push_token';

// Claimed synchronously by `PushSession.init()` to defend against concurrent
// init calls — without a synchronous claim, two awaits would both pass the
// `appStateHandle == null` idempotency check and stack duplicate listeners.
const INITIALIZING_SENTINEL: PluginListenerHandle = {
  remove: async () => {},
};

function getStoredToken(): string | null {
  return typeof window !== 'undefined'
    ? window.localStorage.getItem(PUSH_TOKEN_KEY)
    : null;
}

function storeToken(token: string | null) {
  if (typeof window === 'undefined') return;
  if (token) {
    window.localStorage.setItem(PUSH_TOKEN_KEY, token);
  } else {
    window.localStorage.removeItem(PUSH_TOKEN_KEY);
  }
}

// The `Badge` plugin is a native-only no-op on web (Capacitor proxies the
// call into a "not implemented" rejection). The early-return on
// `isNativePlatform()` is load-bearing — every Badge.* call below assumes
// the guard ran. Do not move the Badge calls outside this function without
// preserving the guard.
async function setBadgeCount(count: number): Promise<void> {
  if (!isNativePlatform()) return;
  // Defend against API drift / accidental string-typed unread counts that
  // would otherwise reach the native plugin as NaN and silently corrupt
  // the icon badge.
  if (!Number.isFinite(count)) {
    console.error('Invalid badge count:', count);
    return;
  }
  try {
    if (count <= 0) {
      await Badge.clear();
    } else {
      await Badge.set({ count });
    }
  } catch (err) {
    console.error('Failed to set badge:', err);
  }
}

/**
 * Reads the user's authoritative unread count from the server and writes it
 * to the iOS/Android icon badge via the native plugin. Used in three places:
 *
 *  1. Right after `register()` finishes (initial sync on app launch)
 *  2. The `appStateChange` listener when the app foregrounds
 *  3. After `markAsRead` succeeds (called from message components) — this
 *     guarantees the same-device badge update without round-tripping through
 *     APNs, since silent pushes are throttled and unreliable on iOS.
 *
 * The server response is `{ totalUnreadCount, changeSet: [...] }` — we use
 * the pre-aggregated `totalUnreadCount` directly rather than summing the
 * array, both for correctness and to keep the math in one place.
 *
 * **Call sparingly.** Each invocation hits `/api9/users/.../conversations/changeset`
 * (non-trivial SQL) and writes to the native Badge plugin. Do NOT call from
 * a render path or unguarded `useEffect`. Intended trigger points are listed
 * above — if you need a new one, prefer hooking it next to an existing
 * state-changing action (like `markAsRead`) rather than polling.
 */
export async function syncBadgeFromServer(): Promise<void> {
  if (!isNativePlatform()) return;
  const username = encodedUsername();
  if (!username) return;
  try {
    const res = await axios.get<{ totalUnreadCount?: number }>(
      `/api9/users/${username}/conversations/changeset`
    );
    const raw = res.data?.totalUnreadCount;
    // Warn loudly when the expected field is null/missing — this is how
    // the previous shape bug stayed hidden for weeks (silent fallback to
    // 0). The `== null` check catches both `undefined` (field omitted)
    // and `null` (server returned a null value), which is the same set
    // of cases the `?? 0` fallback masks. If you're seeing this in logs,
    // the API contract changed; update the consumer and the test fixtures.
    if (raw == null) {
      console.warn(
        '[syncBadgeFromServer] Response missing totalUnreadCount; defaulting to 0. ' +
          'Possible API shape drift — verify /conversations/changeset response.'
      );
    }
    const total = raw ?? 0;
    await setBadgeCount(total);
  } catch (err) {
    console.error('Failed to sync badge from server:', err);
  }
}

/**
 * Encapsulates per-session push-notification listener state.
 *
 * **Single-instance only.** `PushNotifications.addListener` and
 * `App.addListener` write to global plugin namespaces, so two simultaneously
 * active sessions would stack listeners on the same plugin instance and
 * fire duplicate side effects. Production code should use the
 * `defaultSession` via `initializePushNotifications` /
 * `unregisterPushNotifications`. Tests may construct `new PushSession()`
 * per test for isolation but must not run two sessions concurrently
 * against the same Capacitor plugin instance.
 */
export class PushSession {
  private appStateHandle: PluginListenerHandle | null = null;

  async init(navigateTo: (path: string) => void): Promise<void> {
    if (!isNativePlatform()) return;

    // Idempotency guard. The sentinel claim is synchronous so a second
    // concurrent call sees a non-null handle immediately — without it,
    // two awaits would both pass the null-check and stack listeners.
    if (this.appStateHandle) return;
    this.appStateHandle = INITIALIZING_SENTINEL;

    try {
      // Listeners MUST be attached before register() — Capacitor delivers
      // the 'registration' event synchronously once iOS/Android returns
      // the token, and there is no replay for late subscribers. Awaiting
      // each addListener pins the invariant on the type system rather
      // than on undocumented sync behavior of the plugin.
      await PushNotifications.addListener('registration', async (token) => {
        storeToken(token.value);
        const username = encodedUsername();
        if (!username) return;
        try {
          await axios.post(`/api9/push/${username}/devices`, {
            deviceToken: token.value,
            platform: getPlatform(),
          });
        } catch (err) {
          console.error('Failed to register device token:', err);
        }
      });

      await PushNotifications.addListener('registrationError', (error) => {
        console.error('Push registration error:', error);
      });

      // Foreground notification display is handled by the OS via
      // presentationOptions in capacitor.config.ts — no JS handler needed.

      await PushNotifications.addListener(
        'pushNotificationActionPerformed',
        async (action) => {
          const data = action.notification.data;

          // User tapped a notification → they're "caught up" on this
          // device. Clear delivered banners and resync the icon badge.
          try {
            await PushNotifications.removeAllDeliveredNotifications();
          } catch (err) {
            console.error('Failed to clear delivered notifications:', err);
          }
          void syncBadgeFromServer();

          if (!data?.type) return;

          switch (data.type) {
            case 'message':
              if (data.conversationId) {
                navigateTo(toConversationDetail(Number(data.conversationId)));
              }
              break;
            case 'application':
              if (data.jobId) {
                navigateTo(toJobPostingSummary(Number(data.jobId)));
              }
              break;
            case 'applicationUpdate':
            case 'invitation':
              navigateTo(toMyApplications);
              break;
            case 'interview':
              navigateTo(toInterviews);
              break;
            case 'jobMatch':
              if (data.jobId) {
                navigateTo(toJobSearchJobDetail(data.jobId));
              }
              break;
          }
        }
      );

      // Belt-and-suspenders: silent pushes can be throttled by APNs
      // (~3/hr), so resync the badge whenever the user foregrounds the
      // app. Replaces the sentinel with the real handle.
      this.appStateHandle = await App.addListener(
        'appStateChange',
        async ({ isActive }) => {
          if (!isActive) return;
          try {
            await PushNotifications.removeAllDeliveredNotifications();
          } catch (err) {
            console.error('Failed to clear delivered notifications:', err);
          }
          void syncBadgeFromServer();
        }
      );

      const permResult = await PushNotifications.checkPermissions();
      if (permResult.receive === 'prompt') {
        const requestResult = await PushNotifications.requestPermissions();
        if (requestResult.receive !== 'granted') {
          // User opted out — don't continue running side-effecting
          // listeners (e.g. /conversations/changeset on every foreground)
          // on behalf of someone who declined push. Don't touch the
          // badge: the unread count is independent of push permission
          // and may already reflect real server state.
          await this.detachListeners();
          return;
        }
      } else if (permResult.receive !== 'granted') {
        await this.detachListeners();
        return;
      }

      await PushNotifications.register();

      // Initial sync so a fresh launch shows the correct badge before
      // any server-side silent push arrives.
      void syncBadgeFromServer();
    } catch (err) {
      // Unwind listeners so a retry can succeed. The badge is
      // intentionally NOT touched here — a transient init failure
      // (APNs glitch, plugin throw) doesn't change the user's actual
      // unread count, so clearing the icon would lie about server state.
      await this.detachListeners();
      throw err;
    }
  }

  async unregister(): Promise<void> {
    if (!isNativePlatform()) return;

    const token = getStoredToken();
    const username = encodedUsername();

    // Server-side cleanup is conditional on having both the token and
    // the user — those identify the row to delete.
    if (token && username) {
      try {
        await axios.delete(
          `/api9/push/${username}/devices/${encodeURIComponent(token)}`
        );
      } catch (err) {
        console.error('Failed to unregister device token:', err);
      }
    }

    // Local cleanup runs unconditionally — listeners must be released
    // even when there's nothing to delete server-side. Sign-out is the
    // one path that DOES clear the badge, since the user is leaving and
    // any prior unread count belongs to a different session.
    await this.detachListeners();
    await setBadgeCount(0);
    storeToken(null);
  }

  /**
   * Detach all listeners and reset session state. Used by `unregister`,
   * the `init` failure path, and the permission-denial paths.
   *
   * Deliberately does NOT touch the badge — the badge reflects server
   * unread count and is independent of push-listener lifecycle. Sign-out
   * clears the badge separately in `unregister()`; init failures and
   * permission denials leave it alone.
   *
   * Each step is independent so a failure in one cannot block the rest.
   */
  private async detachListeners(): Promise<void> {
    try {
      await PushNotifications.removeAllListeners();
    } catch (err) {
      console.error('Failed to remove push listeners:', err);
    }
    // Skip remove() on the sentinel (a no-op handle from a partially-
    // failed init) but still release the slot so re-init can proceed.
    if (this.appStateHandle && this.appStateHandle !== INITIALIZING_SENTINEL) {
      try {
        await this.appStateHandle.remove();
      } catch (err) {
        console.error('Failed to remove app-state listener:', err);
      }
    }
    this.appStateHandle = null;
  }
}

// Default session backing the module-level public API. Production has one
// PushSession over the app's lifetime; tests should construct their own
// `new PushSession()` instead of going through these wrappers.
const defaultSession = new PushSession();

export function initializePushNotifications(
  navigateTo: (path: string) => void
): Promise<void> {
  return defaultSession.init(navigateTo);
}

export function unregisterPushNotifications(): Promise<void> {
  return defaultSession.unregister();
}
